import React, { useState, useEffect } from 'react';
import api from '../services/api';

const fmt = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const UtilitiesReport = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [scope, setScope] = useState('utilities');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await api.getUtilitiesReport(year, scope);
        setData(result);
      } catch (e) {
        console.error('Failed to load utilities report:', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year, scope]);

  const downloadCsv = () => {
    if (!data) return;
    const rows = [
      ['Utilities Report', data.scopeLabel, data.year],
      [],
      ['Month', 'Location', 'Bill', 'Vendor', 'Paid', 'Amount', 'Typical Amount'],
      ...data.monthlyDetail.map(d => [
        d.monthName, d.location, d.utility, d.vendor || '',
        d.isPaid ? 'Yes' : 'No', d.amount || 0, d.typicalAmount || 0
      ]),
      [],
      ['YTD Total', data.summary.ytdActual],
      ['Monthly Run Rate', data.summary.monthlyRunRate],
      ['Projected Remainder (Historical)', data.summary.projectedRemainderHistorical],
      ['Full Year Estimate', data.summary.fullYearEstimateHistorical]
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `utilities_report_${data.year}_${data.scope}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-center py-8 text-neutral-500">Loading utilities report...</div>;
  if (!data) return <div className="text-center py-8 text-neutral-500">Failed to load report.</div>;

  const { summary, byMonth, ytdByUtility, ytdByLocation, projections, dataQuality } = data;
  const ytdMonths = byMonth.filter(m => m.month <= data.currentMonth);

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'monthly', label: 'Monthly Detail' },
    { id: 'ytd', label: 'YTD Breakdown' },
    { id: 'projections', label: 'Projections' },
    { id: 'quality', label: 'Data Quality' }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={e => setYear(parseInt(e.target.value))}
            className="px-2 py-1 text-sm border border-neutral-200 rounded"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={scope}
            onChange={e => setScope(e.target.value)}
            className="px-2 py-1 text-sm border border-neutral-200 rounded"
          >
            <option value="utilities">Utilities Only</option>
            <option value="all">All Facility Bills</option>
          </select>
        </div>
        <button
          onClick={downloadCsv}
          className="px-3 py-1.5 text-sm border border-neutral-200 rounded hover:bg-neutral-50"
        >
          Export CSV
        </button>
      </div>

      <p className="text-xs text-neutral-500">{data.scopeLabel} — read-only report from bill payment records</p>

      <div className="flex gap-2 border-b border-neutral-200">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${activeSection === s.id ? 'border-neutral-800 text-neutral-800' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {activeSection === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="text-xs text-neutral-500">YTD Actual</div>
              <div className="text-2xl font-bold">{fmt(summary.ytdActual)}</div>
              <div className="text-xs text-neutral-400">Jan–{data.byMonth[data.currentMonth - 1]?.name}</div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="text-xs text-neutral-500">Monthly Run Rate</div>
              <div className="text-2xl font-bold">{fmt(summary.monthlyRunRate)}</div>
              <div className="text-xs text-neutral-400">avg across {summary.monthsWithPayments} months w/ payments</div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="text-xs text-neutral-500">Projected Remainder</div>
              <div className="text-2xl font-bold text-amber-700">{fmt(summary.projectedRemainderHistorical)}</div>
              <div className="text-xs text-neutral-400">historical avg method</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-xs text-blue-600">Full Year Estimate</div>
              <div className="text-2xl font-bold text-blue-800">{fmt(summary.fullYearEstimateHistorical)}</div>
              <div className="text-xs text-blue-500">YTD + projected remainder</div>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Monthly Totals (YTD)</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {ytdMonths.map(m => (
                <div key={m.month} className={`p-2 rounded border text-center ${m.total === 0 ? 'bg-amber-50 border-amber-200' : 'bg-neutral-50 border-neutral-200'}`}>
                  <div className="text-xs text-neutral-500">{m.name.slice(0, 3)}</div>
                  <div className="font-mono font-medium text-sm">{fmt(m.total)}</div>
                  {m.unpaidCount > 0 && (
                    <div className="text-[10px] text-amber-600">{m.unpaidCount} unpaid</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeSection === 'monthly' && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                <th className="px-3 py-2">Month</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Bill</th>
                <th className="px-3 py-2">Vendor</th>
                <th className="px-3 py-2">Freq</th>
                <th className="px-3 py-2 text-center">Paid</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Typical</th>
              </tr>
            </thead>
            <tbody>
              {data.monthlyDetail
                .filter(d => d.month <= data.currentMonth)
                .map((d, i) => (
                  <tr key={i} className={`border-b border-neutral-100 ${!d.isPaid ? 'bg-amber-50/50' : ''}`}>
                    <td className="px-3 py-1.5">{d.monthName.slice(0, 3)}</td>
                    <td className="px-3 py-1.5">{d.location}</td>
                    <td className="px-3 py-1.5">{d.utility}</td>
                    <td className="px-3 py-1.5 text-neutral-500">{d.vendor}</td>
                    <td className="px-3 py-1.5 text-xs text-neutral-400">{d.frequency}</td>
                    <td className="px-3 py-1.5 text-center">{d.isPaid ? '✓' : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{d.isPaid ? fmt(d.amount) : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-neutral-400">{d.typicalAmount > 0 ? fmt(d.typicalAmount) : '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {activeSection === 'ytd' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">YTD by Utility Type</h3>
            <div className="space-y-2 text-sm">
              {ytdByUtility.map(u => (
                <div key={u.name} className="flex justify-between">
                  <span>{u.name}</span>
                  <div className="text-right">
                    <span className="font-mono font-medium">{fmt(u.total)}</span>
                    <span className="text-xs text-neutral-400 ml-2">({fmt(u.avgMonthly)}/mo)</span>
                  </div>
                </div>
              ))}
              {ytdByUtility.length === 0 && <div className="text-neutral-400">No paid bills recorded YTD</div>}
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">YTD by Location</h3>
            <div className="space-y-2 text-sm">
              {ytdByLocation.map(l => (
                <div key={l.name} className="flex justify-between">
                  <span>{l.name}</span>
                  <div className="text-right">
                    <span className="font-mono font-medium">{fmt(l.total)}</span>
                    <span className="text-xs text-neutral-400 ml-2">({fmt(l.avgMonthly)}/mo)</span>
                  </div>
                </div>
              ))}
              {ytdByLocation.length === 0 && <div className="text-neutral-400">No paid bills recorded YTD</div>}
            </div>
          </div>
          {summary.manualExpensesYtd > 0 && (
            <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <span className="font-medium">Manual Utilities Expenses YTD:</span>{' '}
              <span className="font-mono">{fmt(summary.manualExpensesYtd)}</span>
              <span className="text-neutral-500 ml-2">(entered in Expenses, not Bill Checklist)</span>
            </div>
          )}
        </div>
      )}

      {activeSection === 'projections' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="text-xs text-neutral-500">YTD Actual</div>
              <div className="text-xl font-bold">{fmt(summary.ytdActual)}</div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="text-xs text-neutral-500">Historical Projection (remainder)</div>
              <div className="text-xl font-bold text-amber-700">{fmt(summary.projectedRemainderHistorical)}</div>
              <div className="text-xs text-neutral-400 mt-1">{projections.methods.historical.description}</div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="text-xs text-neutral-500">Budget Projection (remainder)</div>
              <div className="text-xl font-bold">{fmt(summary.projectedRemainderBudget)}</div>
              <div className="text-xs text-neutral-400 mt-1">{projections.methods.budget.description}</div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex justify-between items-center">
            <div>
              <div className="text-sm font-medium text-blue-800">Full Year {data.year} Estimate (Historical)</div>
              <div className="text-xs text-blue-600">YTD actual + projected remaining months</div>
            </div>
            <div className="text-2xl font-bold text-blue-800">{fmt(summary.fullYearEstimateHistorical)}</div>
          </div>

          {projections.byMonth.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                    <th className="px-3 py-2">Month</th>
                    <th className="px-3 py-2 text-right">Historical Est.</th>
                    <th className="px-3 py-2 text-right">Budget Est.</th>
                    <th className="px-3 py-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {projections.byMonth.map(p => (
                    <tr key={p.month} className="border-b border-neutral-100">
                      <td className="px-3 py-2">{p.name}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(p.historical)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(p.budget)}</td>
                      <td className="px-3 py-2 text-xs text-neutral-400">
                        {p.isPartial ? `Partial — ${fmt(p.actualSoFar)} paid so far` : 'Projected'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'quality' && (
        <div className="space-y-4">
          {dataQuality.unpaidBills.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-medium text-amber-800 mb-2">Unpaid Bills YTD ({dataQuality.unpaidBills.length})</h3>
              <div className="space-y-1 text-sm max-h-48 overflow-y-auto">
                {dataQuality.unpaidBills.map((b, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{b.monthName} — {b.location} {b.bill} ({b.vendor})</span>
                    {b.typicalAmount > 0 && <span className="font-mono text-neutral-500">~{fmt(b.typicalAmount)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {dataQuality.monthsWithNoPayments.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-800 mb-2">Months With No Payments Recorded</h3>
              <p className="text-sm text-red-700">
                {dataQuality.monthsWithNoPayments.map(m => m.name).join(', ')}
              </p>
            </div>
          )}
          {dataQuality.missingTypicalAmount.length > 0 && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <h3 className="font-medium mb-2">Bills Missing Typical Amount ({dataQuality.missingTypicalAmount.length})</h3>
              <p className="text-xs text-neutral-500 mb-2">Budget projections fall back to YTD average for these.</p>
              <div className="space-y-1 text-sm max-h-32 overflow-y-auto">
                {dataQuality.missingTypicalAmount.map((b, i) => (
                  <div key={i}>{b.location} — {b.bill} ({b.vendor})</div>
                ))}
              </div>
            </div>
          )}
          {dataQuality.unpaidBills.length === 0 && dataQuality.monthsWithNoPayments.length === 0 && (
            <div className="text-center py-6 text-green-700 bg-green-50 border border-green-200 rounded-lg">
              All due bills are marked paid for YTD months.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UtilitiesReport;
